// ISDP CUSTOM: the top of every Details tab — the entity's title, and the row of
// page-level affordances beneath it (star this entry, jump to its source, and when it
// was last touched). One component per Details tab; it used to be the same title
// block plus a five-line source-bar block copy-pasted onto four of them.
import { useEntity, useStarredEntities } from '@backstage/plugin-catalog-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { EntitySourceButton } from '../common/SourceButton';
import { getLastUpdated } from './entityHelpers';
import { docsMessages } from '../../i18n/docs';
import './EntityDetailsHeader.css';

// Backstage's built-in EntityLayout <Header> — which normally renders the entity's
// name at the top of the page — is hidden globally by odx-overrides.css
// (`header[class*="BackstageHeader"] { display:none }`) because we replace the
// Backstage chrome with the ODX top nav. Details is the landing tab for every kind and
// none of its cards states the entity's name, so without this heading the page left
// the user asking "which page am I on?". Shows the display name
// (metadata.title ?? metadata.name) plus a small kind label.
const EntityTitle = () => {
  const { entity } = useEntity();
  const title = entity.metadata.title ?? entity.metadata.name;
  return (
    <div className="devhub-entity-title">
      <span className="devhub-entity-title__kind">{entity.kind}</span>
      <h1 className="devhub-entity-title__name">{title}</h1>
    </div>
  );
};

// Favorites/unfavorites the current entity via Backstage's built-in
// useStarredEntities (localStorage-backed, per-browser — the same store the card
// grids and the header dropdown use). Deliberately the same affordance as the star on
// the docs/catalog cards: an ODX ghost icon-button whose state is carried by the glyph
// (outline vs solid) and aria-pressed, with the label on aria-label/title. It is not
// the shared kit `StarToggle` only because that component's class carries the card's
// absolute corner positioning, which is wrong in this flex row; the markup and the
// labels are the same. `class` (not className) because React 18 does not reflect
// className onto custom elements — see odx.d.ts.
const EntityStarButton = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(docsMessages);
  const { toggleStarredEntity, isStarredEntity } = useStarredEntities();
  const starred = isStarredEntity(entity);
  const label = starred ? t('card.unstar') : t('card.star');
  return (
    <odx-icon-button
      class="devhub-entity-star"
      type="button"
      variant="ghost"
      size="sm"
      icon={starred ? 'core::favorite-solid' : 'core::favorite'}
      aria-pressed={starred}
      aria-label={label}
      title={label}
      onClick={() => toggleStarredEntity(entity)}
    />
  );
};

// Shows the last-updated date stamped by the backend LastAuthorProcessor
// (draeger.com/last-author-date). Renders nothing when the annotation is absent.
const EntityLastUpdated = () => {
  const { entity } = useEntity();
  const date = getLastUpdated(entity);
  if (!date) return null;
  return (
    <div
      className="devhub-entity-last-updated"
      title="Date of most recent catalog-info.yaml commit"
    >
      <odx-icon name="core::time" size="sm" />
      <span>Last updated {date}</span>
    </div>
  );
};

/**
 * One full-width grid row: the entity title on the left, its affordances on the right.
 *
 * Title and affordances used to be two stacked rows, which left the star orphaned at the
 * far left of its own line with "View source" and the date grouped at the far right — three
 * controls that belong together, pushed as far apart as the page allows. Now they are one
 * band: title left, star + source + date together on the right.
 */
export const EntityDetailsHeader = () => (
  <div className="devhub-entity-grid__item--md12 devhub-entity-header">
    <EntityTitle />
    <div className="devhub-entity-header__actions">
      <EntityStarButton />
      <EntitySourceButton />
      <EntityLastUpdated />
    </div>
  </div>
);
